/**
 * Video with Text Section
 * Handles play/pause controls for desktop and mobile videos
 * Compatible with Shopify section events
 */

class VideoWithText {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.videoWithTextSection;
    
    // Query all elements within this section only
    this.desktopVideo = section.querySelector('.custom-video.desktop-video');
    this.mobileVideo = section.querySelector('.custom-video.mobile-video');
    this.desktopBtn = section.querySelector('.play-pause-btn-desktop');
    this.mobileBtn = section.querySelector('.play-pause-btn-mobile');
    
    // Bound methods for event listeners (needed for removeEventListener)
    this.handleDesktopClick = this.handleDesktopClick.bind(this);
    this.handleMobileClick = this.handleMobileClick.bind(this);
    this.handleVideoStateChange = this.handleVideoStateChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    
    this.resizeTimeout = null;
    this.initialized = false;
    
    this.init();
  }
  
  init() {
    if (this.initialized) return;
    
    // Validate we have at least one video and one button
    const hasVideos = this.desktopVideo || this.mobileVideo;
    const hasButtons = this.desktopBtn || this.mobileBtn;
    
    if (!hasVideos || !hasButtons) {
      console.warn('VideoWithText: Missing required elements', {
        section: this.sectionId,
        hasDesktopVideo: !!this.desktopVideo,
        hasMobileVideo: !!this.mobileVideo,
        hasDesktopBtn: !!this.desktopBtn,
        hasMobileBtn: !!this.mobileBtn
      });
      return;
    }
    
    this.attachEventListeners();
    this.updateButtonStates();
    this.initialized = true;
    
    console.log('VideoWithText initialized:', this.sectionId);
  }
  
  attachEventListeners() {
    // Desktop button click
    if (this.desktopBtn && this.desktopVideo) {
      this.desktopBtn.addEventListener('click', this.handleDesktopClick);
    }
    
    // Mobile button click
    if (this.mobileBtn && this.mobileVideo) {
      this.mobileBtn.addEventListener('click', this.handleMobileClick);
    }
    
    // Video state events
    const videoEvents = ['play', 'pause', 'playing', 'loadeddata'];
    [this.desktopVideo, this.mobileVideo].forEach(video => {
      if (video) {
        videoEvents.forEach(eventName => {
          video.addEventListener(eventName, this.handleVideoStateChange);
        });
      }
    });
    
    // Window resize
    window.addEventListener('resize', this.handleResize);
  }
  
  handleDesktopClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!this.desktopVideo) return;
    
    if (this.desktopVideo.paused) {
      this.playVideo(this.desktopVideo);
    } else {
      this.pauseVideo(this.desktopVideo);
    }
  }
  
  handleMobileClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!this.mobileVideo) return;
    
    if (this.mobileVideo.paused) {
      this.playVideo(this.mobileVideo);
    } else {
      this.pauseVideo(this.mobileVideo);
    }
  }
  
  playVideo(targetVideo) {
    if (!targetVideo) return;
    
    // Pause the other video if it exists
    if (targetVideo === this.desktopVideo && this.mobileVideo) {
      this.mobileVideo.pause();
    } else if (targetVideo === this.mobileVideo && this.desktopVideo) {
      this.desktopVideo.pause();
    }
    
    // Play target video
    targetVideo.play()
      .then(() => {
        this.updateButtonStates();
      })
      .catch(err => {
        console.error('VideoWithText: Play error', err);
      });
  }
  
  pauseVideo(targetVideo) {
    if (!targetVideo) return;
    targetVideo.pause();
    this.updateButtonStates();
  }
  
  handleVideoStateChange(e) {
    // Small delay to ensure state is updated
    setTimeout(() => {
      this.updateButtonStates();
    }, 50);
  }
  
  updateButtonStates() {
    // Update desktop button
    if (this.desktopBtn && this.desktopVideo) {
      this.updateButtonState(this.desktopBtn, this.desktopVideo);
    }
    
    // Update mobile button
    if (this.mobileBtn && this.mobileVideo) {
      this.updateButtonState(this.mobileBtn, this.mobileVideo);
    }
  }
  
  updateButtonState(button, video) {
    if (!button || !video) return;
    
    if (video.paused) {
      button.classList.remove('pause');
      button.classList.add('play');
    } else {
      button.classList.remove('play');
      button.classList.add('pause');
    }
  }
  
  handleResize() {
    clearTimeout(this.resizeTimeout);
    
    this.resizeTimeout = setTimeout(() => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      const activeVideo = this.getActiveVideo();
      const inactiveVideo = isMobile ? this.desktopVideo : this.mobileVideo;
      
      // Sync play state when switching between mobile/desktop
      if (activeVideo && inactiveVideo) {
        const shouldPlay = !inactiveVideo.paused;
        inactiveVideo.pause();
        
        if (shouldPlay) {
          this.playVideo(activeVideo);
        }
      }
      
      this.updateButtonStates();
    }, 150);
  }
  
  getActiveVideo() {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    
    if (isMobile && this.mobileVideo) {
      return this.mobileVideo;
    }
    
    if (!isMobile && this.desktopVideo) {
      return this.desktopVideo;
    }
    
    // Fallback to first available video
    return this.desktopVideo || this.mobileVideo;
  }
  
  destroy() {
    // Remove all event listeners
    if (this.desktopBtn) {
      this.desktopBtn.removeEventListener('click', this.handleDesktopClick);
    }
    
    if (this.mobileBtn) {
      this.mobileBtn.removeEventListener('click', this.handleMobileClick);
    }
    
    const videoEvents = ['play', 'pause', 'playing', 'loadeddata'];
    [this.desktopVideo, this.mobileVideo].forEach(video => {
      if (video) {
        videoEvents.forEach(eventName => {
          video.removeEventListener(eventName, this.handleVideoStateChange);
        });
      }
    });
    
    window.removeEventListener('resize', this.handleResize);
    
    clearTimeout(this.resizeTimeout);
    this.initialized = false;
    
    console.log('VideoWithText destroyed:', this.sectionId);
  }
}

// Store instances to manage lifecycle
const videoInstances = new Map();

/**
 * Initialize a video section
 */
function initVideoSection(container) {
  const section = container.querySelector('[data-video-with-text-section]');
  
  if (!section) {
    console.warn('VideoWithText: Section element not found');
    return;
  }
  
  const sectionId = section.dataset.videoWithTextSection;
  
  // Destroy existing instance if it exists
  if (videoInstances.has(sectionId)) {
    videoInstances.get(sectionId).destroy();
  }
  
  // Create new instance
  const instance = new VideoWithText(section);
  videoInstances.set(sectionId, instance);
}

/**
 * Destroy a video section
 */
function destroyVideoSection(container) {
  const section = container.querySelector('[data-video-with-text-section]');
  
  if (!section) return;
  
  const sectionId = section.dataset.videoWithTextSection;
  
  if (videoInstances.has(sectionId)) {
    videoInstances.get(sectionId).destroy();
    videoInstances.delete(sectionId);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-video-with-text-section]').forEach(section => {
      initVideoSection(section.closest('.shopify-section') || document);
    });
  });
} else {
  // DOM already loaded
  document.querySelectorAll('[data-video-with-text-section]').forEach(section => {
    initVideoSection(section.closest('.shopify-section') || document);
  });
}

// Shopify Theme Editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initVideoSection(event.target);
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    destroyVideoSection(event.target);
  });
  
  document.addEventListener('shopify:section:reorder', (event) => {
    // Reinitialize on reorder to ensure proper state
    destroyVideoSection(event.target);
    setTimeout(() => initVideoSection(event.target), 100);
  });
}
